import type { NavigationRouter } from "@app/navigation";
import type { SupportedLocale } from "@shared/i18n/types";
import type { QueryClient } from "@tanstack/react-query";

import { type ReactNode, useMemo } from "react";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { AuthenticationProvider } from "@features/authentication";
import { CaptchaProvider } from "@features/captcha/provider";

import { NavigationProvider } from "@app/navigation/provider";
import { StartupGuard } from "@app/ui";
import { ApiProvider } from "@shared/api/provider";
import { FTProvider } from "@shared/feature-toggle";
import { getMessagesByLocale } from "@shared/i18n";
import { LocalesProvider } from "@shared/i18n-react-intl";
import { DEFAULT_LOCALE } from "@shared/i18n/constants";
import { PathfinderProvider } from "@shared/pathfinder";
import { ToastProvider } from "@shared/ui/chakra/feedback/toast";
import { OverlaysProvider } from "@shared/ui/chakra/overlays/provider";
import { ThemeProvider } from "@shared/ui/theme";

type ProvidersProps = {
  children?: ReactNode;
  nonce?: string;
  queryClient: QueryClient;
  router: NavigationRouter;
};

export const Providers = ({
  children,
  nonce,
  queryClient,
  router,
}: ProvidersProps) => {
  const cssCache = useMemo(
    () => createCache({ key: "kex-web", nonce }),
    [nonce],
  );

  return (
    <FTProvider>
      <AuthenticationProvider>
        <ApiProvider nonce={nonce} queryClient={queryClient}>
          <CacheProvider value={cssCache}>
            <ThemeProvider>
              <OverlaysProvider>
                <StartupGuard>
                  <ToastProvider>
                    <PathfinderProvider>
                      <LocalesProvider<SupportedLocale>
                        defaultLocale={DEFAULT_LOCALE}
                        messagesGetter={getMessagesByLocale}
                        renderChildren={(locale) => {
                          return (
                            <CaptchaProvider language={locale}>
                              <NavigationProvider
                                queryClient={queryClient}
                                router={router}
                              >
                                {children}
                              </NavigationProvider>
                            </CaptchaProvider>
                          );
                        }}
                      />
                    </PathfinderProvider>
                  </ToastProvider>
                </StartupGuard>
              </OverlaysProvider>
            </ThemeProvider>
          </CacheProvider>
        </ApiProvider>
      </AuthenticationProvider>
    </FTProvider>
  );
};
